// Shared Module Action Rule evaluator — used by both ListView.jsx (List, one
// evaluation per selected row) and DetailHeaderCard.jsx (Detail, one
// evaluation for the single record on screen) so there is exactly one
// implementation of the 7 operators configured in
// pages/Modules/Admin/ModuleActionRulesSection.jsx, instead of two files that
// can silently drift apart (which is how Detail's `scope` operator went dead
// — it compared against a hardcoded literal instead of the real view scope).
//
// `scope` conditions compare against whatever "view scope" string the caller
// passes in (e.g. the active List tab key like "myJobs"/"allJobs"). Callers
// with no real scope concept (e.g. a Detail page) should pass `undefined` —
// a rule can then only match `scope` conditions if the admin never configured
// one for that context, same as any other field that isn't present.

// Resolves a rule's field path against the record, tolerating BOTH record
// shapes the two callers produce: List rows are the raw nested DB document
// (rule path "a.b" → record.a.b), while the Detail header flattens form-group
// fields under their configured field keys — which may be the dotted path
// itself ("a.b" as a literal key) or just the leaf ("b", e.g. the submission
// form's internalSubmissionStatusText vs the rule's nested
// internalSubmissionStatus.internalSubmissionStatusText). Trying literal →
// nested → leaf keeps one admin rule working against every caller with no
// per-module code.
// Walks a dotted path, and when it meets an ARRAY it maps the remaining path
// over every element and flattens the result. That makes a path like
// "recruiters.recruiterId" resolve to [1, 7, …] on a record whose `recruiters`
// is an array of objects — which is what an array-membership condition (e.g.
// "is this job already assigned to me?") needs. Scalar paths are unaffected.
function resolvePath(value, keys) {
  if (value === undefined || value === null) return undefined;
  if (keys.length === 0) return value;
  if (Array.isArray(value)) {
    const mapped = value
      .map((item) => resolvePath(item, keys))
      .filter((item) => item !== undefined && item !== null);
    return mapped.flat();
  }
  return resolvePath(value[keys[0]], keys.slice(1));
}

function getNestedValue(record, fieldKey) {
  if (!fieldKey || typeof fieldKey !== 'string' || !record) return undefined;
  if (fieldKey in record) return record[fieldKey];
  const nested = resolvePath(record, fieldKey.split('.'));
  if (nested !== undefined && !(Array.isArray(nested) && nested.length === 0)) return nested;
  const leaf = fieldKey.split('.').pop();
  return leaf !== fieldKey ? record[leaf] : undefined;
}

// Runtime tokens an admin can put in a condition's `value`, so a rule can be
// written against "the person looking at the screen" instead of a hardcoded id.
// Resolved at evaluation time from the signed-in user. Unknown tokens are left
// alone, so an ordinary literal value is never altered.
function currentUserId() {
  try {
    return typeof localStorage !== 'undefined' ? (localStorage.getItem('userId') ?? '') : '';
  } catch {
    return '';
  }
}

const RULE_VALUE_TOKENS = {
  '{currentuserid}': currentUserId,
  '{currentuser}': currentUserId,
  '{me}': currentUserId,
};

function resolveRuleValue(value) {
  const token = RULE_VALUE_TOKENS[String(value ?? '').trim().toLowerCase()];
  return token ? token() : value;
}

function normalizeText(value) {
  return String(value ?? '').trim().toLowerCase();
}

function isEmptyRuleValue(value) {
  return value === undefined || value === null || value === ''
    || (Array.isArray(value) && value.length === 0);
}

export function conditionMatches(condition, record, scope) {
  const operator = condition?.operator;
  const actual = operator === 'scope' ? scope : getNestedValue(record, condition?.field);
  const expected = resolveRuleValue(condition?.value);
  const normalizedActual = normalizeText(actual);
  const normalizedExpected = normalizeText(expected);

  if (operator === 'eq' || operator === 'scope') return normalizedActual === normalizedExpected;
  if (operator === 'ne') return normalizedActual !== normalizedExpected;
  if (operator === 'isEmpty') return isEmptyRuleValue(actual);
  if (operator === 'notEmpty') return !isEmptyRuleValue(actual);
  if (operator === 'fieldEq') {
    return normalizedActual === normalizeText(getNestedValue(record, condition?.compareField));
  }
  // EXACT membership — the id-safe counterpart of `contains` (whose substring
  // match would make user "1" match "12"). Use this for id lists, e.g.
  // recruiters.recruiterId includes {currentUserId}.
  if (operator === 'includes' || operator === 'notIncludes') {
    const list = Array.isArray(actual) ? actual : [actual];
    const hit = list.some((item) => normalizeText(item) === normalizedExpected)
      && normalizedExpected !== '';
    return operator === 'includes' ? hit : !hit;
  }
  if (operator === 'contains') {
    if (Array.isArray(actual)) return actual.some((item) => normalizeText(item).includes(normalizedExpected));
    return normalizedActual.includes(normalizedExpected);
  }
  return false;
}

export function isRuleActionBlocked(actionKey, record, rules, scope) {
  const list = Array.isArray(rules) ? rules : [];
  if (!list.length) return false;
  return list.some((rule) => (
    rule?.isActive !== false
    && (rule?.blockedActions ?? []).map(String).includes(String(actionKey))
    && (rule?.conditions ?? []).every((condition) => conditionMatches(condition, record, scope))
  ));
}

// isRuleActionDisabled — the same admin-configured rule shape as blockedActions,
// but the action stays VISIBLE and just goes unclickable instead of vanishing
// (e.g. Approve/Reject during a "Resubmission" review cycle: the user should
// still see the buttons and understand why they can't act, not wonder where
// the actions went). Returns the matched rule (so the caller can surface its
// `description` as the disabled reason) or null.
export function ruleActionDisabledReason(actionKey, record, rules, scope) {
  const list = Array.isArray(rules) ? rules : [];
  const rule = list.find((r) => (
    r?.isActive !== false
    && (r?.disabledActions ?? []).map(String).includes(String(actionKey))
    && (r?.conditions ?? []).every((condition) => conditionMatches(condition, record, scope))
  ));
  return rule ? (rule.description || `${actionKey} is temporarily unavailable`) : null;
}

// actionDoneState — config-driven "done" terminus. When the action's
// admin-configured `doneWhen` condition matches the record, the action stops
// being clickable and renders `doneLabel` (in `doneTone`) instead of its normal
// label — e.g. "Move to Onboarding" becomes a disabled "Moved to Onboarded"
// once the submission has been onboarded. Reuses the same condition evaluator
// as the visibility rules. Returns { label, tone } or null. Shared so the List,
// the row-action dropdown and the Detail header all render it identically.
export function actionDoneState(action, record, scope) {
  const condition = action?.doneWhen;
  if (!condition || !condition.field) return null;
  if (!conditionMatches(condition, record, scope)) return null;
  return {
    label: action.doneLabel || action.label,
    tone: action.doneTone || 'success',
  };
}

// isActionBlocked — List view's original signature: blocked if ANY of the
// given records would have this action blocked (used for the bulk multi-select
// toolbar, where one action covers every selected row at once).
export function isActionBlocked(actionKey, records, rules, scope) {
  const list = Array.isArray(records) ? records : [];
  if (!list.length) return false;
  return list.some((record) => isRuleActionBlocked(actionKey, record, rules, scope));
}
